import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { tap } from 'rxjs';

/**
 * IT-F2-391 item 32fbb7b4: stamp X-F2-Session-ID on every outbound admin-SPA
 * HTTP request. Per-tab UUID, stored in sessionStorage so intra-tab reloads
 * keep the same id (tab reclaims its own live-session slot); new tabs get
 * fresh ids and displace the prior tab per the fresh-auth-wins rule.
 *
 * Fallback to in-memory when sessionStorage is unavailable (private mode /
 * strict iframe). Backend's ensureSessionId + skip-claim gates on
 * from_header, and generated ids never claim, so in-memory sessions are
 * a safe no-op for multi-window enforcement.
 *
 * Adopts server-echoed ids on the response: when middleware synthesizes
 * a session id and stamps X-F2-Session-ID on the response, we swap it into
 * sessionStorage so the next request claims the slot with the id the server
 * already knows about.
 */

const SESSION_ID_KEY = 'f2_session_id';
let memFallback: string | null = null;

function generateId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* fall through to random */ }
  return `mem-${Math.random().toString(36).slice(2, 12)}-${Date.now().toString(36)}`;
}

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = generateId();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    if (!memFallback) memFallback = generateId();
    return memFallback;
  }
}

function adoptEchoedId(headers: { get(name: string): string | null } | null | undefined): void {
  try {
    if (!headers || typeof headers.get !== 'function') return;
    const echoed = headers.get('X-F2-Session-ID') || headers.get('x-f2-session-id');
    if (!echoed) return;
    const current = getSessionId();
    if (current === echoed) return;
    try { sessionStorage.setItem(SESSION_ID_KEY, echoed); } catch { memFallback = echoed; }
  } catch { /* header parse failure — quiet no-op */ }
}

export const sessionIdInterceptor: HttpInterceptorFn = (req, next) => {
  // Don't overwrite if the caller already set it (e.g. an integration test
  // pinning a specific id).
  if (req.headers.has('X-F2-Session-ID')) return next(req);
  const stamped = req.clone({ setHeaders: { 'X-F2-Session-ID': getSessionId() } });
  return next(stamped).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) adoptEchoedId(event.headers as any);
      },
      error: (err) => {
        // Server may echo the id even on error responses (e.g. 401 with X-F2-Session-ID)
        try { adoptEchoedId(err?.headers); } catch { /* ignore */ }
      },
    }),
  );
};
